import api from '../api/api';
/**
 * Fetches all active models and their attributes by map id
 * @param  {number} mapId - id from search params
 */
export const fetchActiveModels = (mapId) => {
  return async (dispatch, getState) => {
    try {
      const { data } = await api.getActiveModels(mapId);
      data.forEach((model) => {
        const textures = model.textures ? JSON.parse(model.textures) : null;
        const location_ids = model.location_ids
          ? JSON.parse(model.location_ids)
          : null;
        dispatch(
          saveModelAttributes(model.id, { ...model, location_ids, textures })
        );
      });
    } catch (error) {
      dispatch(errorMessage(error.message));
    }
  };
};

/**
 * Fetches all inactive models and their attributes by map id
 * @param  {number} mapId - id from search params
 */
export const fetchInactiveModels = (mapId) => {
  return async (dispatch, getState) => {
    try {
      const { data } = await api.getInactiveModels(mapId);
      data.forEach((model) => {
        dispatch(saveInactiveModel(model.id, model));
      });
    } catch (error) {
      dispatch(errorMessage(error.message));
    }
  };
};

/**
 * Fetches a single models attributes
 * @param  {string} id - the model's id
 * @param  {boolean} grabFile - boolean to also fetch the model file
 */
export const fetchModel = (id, grabFile) => {
  return async (dispatch, getState) => {
    const { map } = getState().config;
    try {
      let { data } = await api.getModelAttributes(map, id);
      let [model] = data;
      const textures = model.textures ? JSON.parse(model.textures) : null;
      const location_ids = model.location_ids
        ? JSON.parse(model.location_ids)
        : null;
      dispatch(
        saveModelAttributes(model.id, { ...model, textures, location_ids })
      );
      if (grabFile) {
        dispatch(fetchModelFile(model.id, model.name));
      }
    } catch (error) {
      dispatch(errorMessage(error.message));
    }
  };
};

/**
 * Fetches a model file as a blob
 * @param  {string} id - the model's id
 * @param  {string} fileName - the name of the model file
 */
export const fetchModelFile = (id, fileName, replace) => {
  return async (dispatch, getState) => {
    const { map } = getState().config;
    const { modelBlobs } = getState().models;
    if (
      replace ||
      !Object.prototype?.hasOwnProperty?.call(modelBlobs || {}, id)
    ) {
      dispatch(saveBlob(id, 'modelBlobs', null));
      try {
        const { data } = await api.getFile(map, id, fileName);
        const blob = URL.createObjectURL(data);
        dispatch(saveBlob(id, 'modelBlobs', blob));
      } catch (error) {
        dispatch(errorMessage(error.message));
        dispatch(saveBlob(id, 'modelBlobs', null));
      }
    }
  };
};

/**
 * Fetches a thumbnail as a blob
 * @param  {string} id - the model's id
 */
export const fetchThumbnail = (id, replace) => {
  return async (dispatch, getState) => {
    const { map } = getState().config;
    const { thumbBlobs } = getState().models;
    if (
      replace ||
      !Object.prototype.hasOwnProperty.call(thumbBlobs || {}, id)
    ) {
      dispatch(saveBlob(id, 'thumbBlobs', null));
      try {
        const { data } = await api.getFile(map, id, 'thumbnail.png');
        const blob = URL.createObjectURL(data);
        dispatch(saveBlob(id, 'thumbBlobs', blob));
      } catch (error) {
        dispatch(errorMessage(error.message));
        dispatch(saveBlob(id, 'thumbBlobs', null));
      }
    }
  };
};

/**
 * Updates a models attributes
 * @param  {string} id - the model's id
 * @param  {object} data - the model's attributes
 */
export const updateModel = (
  id,
  data,
  shouldTrackSaving = true,
  shouldUpdateImmediately = false
) => {
  return async (dispatch, getState) => {
    const { map } = getState().config;
    const currentModel = getState().models?.activeModels?.[id];
    const previousData = shouldUpdateImmediately
      ? Object.keys(data).reduce((modelData, key) => {
          modelData[key] = currentModel?.[key];
          return modelData;
        }, {})
      : null;

    if (shouldTrackSaving) {
      dispatch(setIsSaving());
    }
    if (shouldUpdateImmediately) {
      dispatch(saveModelAttributes(id, data));
    }
    try {
      const res = await api.updateModelAttributes(map, id, data);
      if (res.data && !shouldUpdateImmediately) {
        dispatch(saveModelAttributes(id, data));
      }
      if (shouldTrackSaving) {
        dispatch(setIsSaving());
      }
    } catch (error) {
      dispatch(errorMessage(error.message));
      if (shouldUpdateImmediately) {
        dispatch(saveModelAttributes(id, previousData));
      }
      if (shouldTrackSaving) {
        dispatch(setIsSaving());
      }
    }
  };
};

/**
 * Uploads a model
 * @param  {file} model - the model file
 * @param  {function} callback - callback function
 */
export const uploadModelFile = (model, callback) => {
  return async (dispatch, getState) => {
    const { map } = getState().config;
    dispatch(isUploading());
    try {
      const { data } = await api.uploadModel(model, map);
      dispatch(fetchModel(data.id));
      dispatch(isUploading());
      data && typeof callback === 'function' && callback(data.id);
    } catch (error) {
      dispatch(errorMessage(error.message));
      dispatch(isUploading());
    }
  };
};

/**
 * Uploads and replaces an existing model
 * @param  {file} model - the model file
 * @param  {string} id - model id
 * @param  {function} callback - callback function
 */
export const updateModelFile = (model, id, callback) => {
  return async (dispatch, getState) => {
    const { map } = getState().config;
    dispatch(isUploading());
    try {
      const { data } = await api.updateExistingModel(model, map, id);
      dispatch(isUploading());
      // update processing to 0 just for now.
      data && dispatch(updateModel(id, { processing: 0 }));
      data && dispatch(fetchModel(id));
      data && typeof callback === 'function' && callback(id);
    } catch (error) {
      dispatch(errorMessage(error.message));
      dispatch(isUploading());
    }
  };
};

/**
 * Uploads a thumbnail
 * @param  {file} thumbnail - the model file
 * @param  {string} id - models id
 */
export const uploadThumbnail = (thumbnail, id) => {
  return async (dispatch, getState) => {
    const { map } = getState().config;
    try {
      const { data } = await api.uploadThumbnail(thumbnail, map, id);
      dispatch(fetchThumbnail(data.id, true));
    } catch (error) {
      dispatch(errorMessage(error.message));
      console.log(error.message);
    }
  };
};

/**
 * Deletes a model
 * @param  {string} id - the model's id
 */
export const deleteModel = (id) => {
  return async (dispatch, getState) => {
    const { map } = getState().config;
    try {
      const res = await api.deleteModel(map, id);
      res && dispatch(deleteModelbyId(id));
    } catch (error) {
      dispatch(errorMessage(error.message));
    }
  };
};

/**
 * Uploads a lod texture
 * @param  {string} id - the model's id
 * @param  {string} texture - the model's texture to be used for lods
 */
export const uploadTextureLod = (texture, id) => {
  return async (dispatch, getState) => {
    const { map } = getState().config;
    dispatch(isUploadingTexture());
    try {
      const { data } = await api.uploadTextureLod(texture, map, id);
      dispatch(updateTextures(id, data.dataUploaded.fileNames));
      dispatch(isUploadingTexture());
    } catch (error) {
      dispatch(isUploadingTexture());
      dispatch(errorMessage(error.message));
    }
  };
};

/**
 * Uploads a lod texture
 * @param  {string} id - the model's id
 * @param  {string} texture - the model's texture to be used for lods
 */
export const deleteTextureLod = (fileName, id) => {
  return async (dispatch, getState) => {
    const { map } = getState().config;
    dispatch(isDeletingTexture());
    try {
      const res = await api.deleteTextureLod(fileName, map, id);
      res && dispatch(deleteTexture(id, fileName));
      dispatch(isDeletingTexture());
    } catch (error) {
      dispatch(errorMessage(error.message));
      dispatch(isDeletingTexture());
    }
  };
};

export const setTextureOrder = (data) => {
  return {
    type: 'SET_TEXTURE_ORDER',
    data,
  };
};

/**
 * toggles uploading state
 */
export const isUploading = () => {
  return {
    type: 'UPLOADING_MODEL',
  };
};

/**
 * toggles saving state
 */
export const setIsSaving = () => {
  return {
    type: 'SAVING_MODEL',
  };
};

/**
 * toggles uploading state
 */
export const isUploadingTexture = () => {
  return {
    type: 'UPLOADING_TEXTURE',
  };
};

/**
 * toggles uploading state
 */
export const isDeletingTexture = () => {
  return {
    type: 'DELETING_TEXTURE',
  };
};

/**
 * toggles uploading state
 */
export const updateTextures = (id, data) => {
  return {
    type: 'UPDATE_TEXTURES',
    id,
    data,
  };
};

/**
 * toggles uploading state
 */
export const deleteTexture = (id, texture) => {
  return {
    type: 'DELETE_TEXTURE',
    id,
    texture,
  };
};

/**
 * Saves a blobUrl
 * @param  {string} id - the model's id
 * @param  {string} key - specify where to store
 * @param  {file} blob - saves URL.createObjectUrl(blob)
 */
export const saveBlob = (id, key, blob) => {
  return {
    type: 'SAVE_BLOB',
    id,
    key,
    blob,
  };
};

/**
 * saves a models attributes
 * @param  {string} id - model id
 * @param  {object} data - models attributes to save
 */
export const saveModelAttributes = (id, data) => {
  return {
    type: 'SAVE_MODEL',
    id,
    data,
  };
};

/**
 * deletes a model from the store
 * @param  {string} id - model id
 */
export const deleteModelbyId = (id) => {
  return {
    type: 'DELETE_MODEL',
    id,
  };
};

/**
 * saves an inactive models attributes
 * @param  {string} id - model id
 * @param  {object} data - models attributes to save
 */
export const saveInactiveModel = (id, data) => {
  return {
    type: 'SAVE_INACTIVE_MODEL',
    id,
    data,
  };
};

/**
 * Saves an error message
 * @param  {string} message - save the error message for displaying
 */
export const errorMessage = (message) => {
  return {
    type: 'ERROR_MESSAGE',
    message: message || 'Something went wrong!',
  };
};
